import { useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { AnalysisResult } from '../components/AnalysisResult';
import { AskKova } from '../components/AskKova';
import { FieldLines } from '../components/Field';
import { ErrorLine } from '../components/ErrorLine';
import { LoadingLine } from '../components/LoadingLine';
import { ProgressSteps } from '../components/ProgressSteps';
import { useStreamingAnalysis } from '../lib/useStreamingAnalysis';

const INTENTS: { k: string; text: string; to: string }[] = [
  { k: 'Predict', text: 'A winner, a margin, and the reasons.', to: '/predict' },
  { k: 'Betting', text: 'Edge, expected value, and a stake.', to: '/betting' },
  { k: 'Grades', text: 'Was that fourth down the right call.', to: '/grades' },
  { k: 'Breakdown', text: 'What actually decided a finished game.', to: '/breakdown' },
];

function friendly(error: string): string {
  const e = error.toLowerCase();
  if (e.includes('budget')) return 'Daily analysis budget reached. Back tomorrow.';
  if (e.includes('limit')) return 'Analysis limit reached. Try again in a few minutes.';
  return 'Something went wrong.';
}

export function AskPage() {
  const [params, setParams] = useSearchParams();
  const nav = useNavigate();
  const { ask, result, steps, loading, error } = useStreamingAnalysis();
  const query = params.get('q') ?? '';

  const submit = (q: string) => { setParams({ q }); void ask(q); };
  useEffect(() => { if (query) void ask(query); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <section className="container pt-2 md:pt-4">
      <div className="relative overflow-hidden bg-blue p-6 text-paper md:p-10">
        <FieldLines />
        <div className="relative max-w-[760px]">
          <p className="kicker !text-paper/80">Ask Kova</p>
          <h1 className="h1 mt-2">Any game. Any question.</h1>
          <div className="mt-6"><AskKova onAsk={submit} loading={loading} onBlue examples={false} /></div>
        </div>
        {!query && (
          <div className="relative mt-6 grid grid-cols-2 gap-2 md:grid-cols-4 md:gap-3">
            {INTENTS.map((i) => (
              <Link key={i.k} to={i.to} className="group flex flex-col items-start gap-1 border-t-2 border-white/40 py-3 text-left transition-colors hover:border-paper">
                <span className="font-display text-[20px] font-extrabold uppercase leading-none tracking-[-0.01em]">{i.k}</span>
                <span className="text-[13px] leading-snug text-paper/85">{i.text}</span>
              </Link>
            ))}
          </div>
        )}
      </div>

      <div className="mx-auto mt-8 max-w-[860px] pb-14">
        {query && (
          <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
            <p className="label">You asked: <span className="font-semibold text-ink">{query}</span></p>
            <button onClick={() => nav('/ask')} className="btn-secondary btn-sm">New question</button>
          </div>
        )}
        <LoadingLine active={loading} />
        <ProgressSteps steps={steps} active={loading} />
        {error && <div className="mt-4"><ErrorLine message={friendly(error)} onRetry={() => query && submit(query)} /></div>}
        {result && <div className="mt-6"><AnalysisResult r={result} /></div>}
      </div>
    </section>
  );
}
